import axios from 'axios';
import { getAccessToken, userManager } from './auth';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 180000,
});

// Attach Cognito JWT (id token) to every request when the user is logged in.
api.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A 401 means the session is gone (expired token, failed silent renew, or a
// revoked session) — drop the locally-cached user so AuthContext flips `user`
// to null and the app's auth gate swaps straight to the login screen, no
// manual "log out" click required.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401 && userManager) {
      try {
        await userManager.removeUser();
      } catch {
        // best-effort — the request itself still rejects below either way
      }
    }
    return Promise.reject(error);
  },
);

export const fetchStats = () => api.get('/stats').then((r) => r.data);
// The home feed is paginated (newest first). `filter` is the sidebar filter
// ('all' | 'cat:<id>' | 'proj:<id>' | '<source_type>'); pass the previous
// response's `next_cursor` to get the page after it.
export const PAGE_SIZE = 25;
export const fetchDocuments = ({ cursor = null, filter = 'all', limit = PAGE_SIZE } = {}) => {
  const params = { limit };
  if (cursor) params.cursor = cursor;
  if (filter?.startsWith('cat:')) params.category_id = filter.slice(4);
  else if (filter?.startsWith('proj:')) params.project_id = filter.slice(5);
  else if (filter && filter !== 'all') params.source_type = filter;
  return api.get('/documents', { params }).then((r) => r.data);
};
export const fetchDocument = (id) => api.get(`/documents/${id}`).then((r) => r.data);

export const ingestText = (content, title = null, category_id = null, project_id = null) =>
  api.post('/ingest/text', { content, title, category_id, project_id }).then((r) => r.data);

export const ingestUrl = (url, title = null, category_id = null, reason = null, project_id = null) =>
  api.post('/ingest/url', { url, title, category_id, reason, project_id }).then((r) => r.data);

export const ingestPdf = (file, category_id = null, reason = null, project_id = null) => {
  const fd = new FormData();
  fd.append('file', file);
  const params = [];
  if (category_id) params.push(`category_id=${encodeURIComponent(category_id)}`);
  if (reason) params.push(`reason=${encodeURIComponent(reason)}`);
  if (project_id) params.push(`project_id=${encodeURIComponent(project_id)}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  return api
    .post(`/ingest/pdf${qs}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((r) => r.data);
};

// `categoryId`/`projectId` scope the search server-side (a real pre-filter on
// the vector query), so a narrow category/project still gets a full top_k of
// its own hits instead of whatever survives filtering a global top_k client-side.
export const searchBrain = (query, topK = 12, categoryId = null, projectId = null) =>
  api
    .post('/search', { query, top_k: topK, category_id: categoryId, project_id: projectId })
    .then((r) => r.data);

export const listCategories = () => api.get('/categories').then((r) => r.data);
export const createCategory = (name, color) =>
  api.post('/categories', { name, color }).then((r) => r.data);
export const updateCategory = (id, name, color) =>
  api.patch(`/categories/${id}`, { name, color }).then((r) => r.data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`).then((r) => r.data);

export const listProjects = () => api.get('/projects').then((r) => r.data);
export const createProject = (name, color) =>
  api.post('/projects', { name, color }).then((r) => r.data);
export const updateProject = (id, name, color) =>
  api.patch(`/projects/${id}`, { name, color }).then((r) => r.data);
export const deleteProject = (id) => api.delete(`/projects/${id}`).then((r) => r.data);

export const deleteDocument = (id) => api.delete(`/documents/${id}`).then((r) => r.data);

export const updateTextDocument = (id, content, title = null, category_id = null, project_id = null) =>
  api.patch(`/documents/${id}`, { content, title, category_id, project_id }).then((r) => r.data);

export const ingestImage = (file, category_id = null, caption, project_id = null) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('caption', caption);
  if (category_id) fd.append('category_id', category_id);
  if (project_id) fd.append('project_id', project_id);
  return api
    .post('/ingest/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((r) => r.data);
};

// Combined note: text + an optional link + N images + N PDFs in one call.
// Each attachment is embedded as its own chunk server-side, so combining
// formats here doesn't blur them into one undifferentiated vector.
export const ingestNote = ({
  body,
  title,
  links = [],
  categoryId,
  projectId,
  images = [],
  pdfs = [],
  audios = [],
} = {}) => {
  const fd = new FormData();
  if (body) fd.append('body', body);
  if (title) fd.append('title', title);
  if (categoryId) fd.append('category_id', categoryId);
  if (projectId) fd.append('project_id', projectId);
  links.forEach((l) => fd.append('links', l));
  images.forEach((f) => fd.append('images', f));
  pdfs.forEach((f) => fd.append('pdfs', f));
  audios.forEach((f) => fd.append('audios', f));
  return api
    .post('/ingest/note', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((r) => r.data);
};

export const updateDocMeta = (id, { title, reason, category_id, project_id } = {}) =>
  api.patch(`/documents/${id}/meta`, { title, reason, category_id, project_id }).then((r) => r.data);

export const getDocumentUrl = (id) =>
  api.get(`/documents/${id}/url`).then((r) => r.data);

// Public, unauthenticated read-only share links — no Bearer token required.
export const getPublicDocument = (owner, docId) =>
  api.get(`/public/documents/${owner}/${docId}`).then((r) => r.data);
export const getPublicDocumentUrl = (owner, docId) =>
  api.get(`/public/documents/${owner}/${docId}/url`).then((r) => r.data);
export const getPublicCollection = (owner, ids) =>
  api
    .get(`/public/documents/${owner}/collection`, { params: { ids: ids.join(',') } })
    .then((r) => r.data);

export default api;
